// src/components/ui/ImagenLightbox.js
// Lightbox global para AMPLIAR imágenes (el patrón "clic en cualquier parte para
// cerrar" de Productos / Consulta Existencias, ahora en un solo lugar).
// "Cambiar uno = cambiar todos." NUNCA abrir imágenes en pestaña nueva
// (target="_blank"); usar este componente.
//
// Uso:
//   import ImagenLightbox from '../ui/ImagenLightbox';
//   const [amp, setAmp] = useState(null);  // { url, nombre }
//   <img onClick={() => setAmp({ url, nombre: 'Producto X' })} style={{ cursor: 'zoom-in' }} />
//   <ImagenLightbox src={amp?.url} nombre={amp?.nombre} onClose={() => setAmp(null)} />
//
// Overlay oscuro a propósito (visor de imagen) — no se tematiza claro/oscuro.
//
// ZOOM Y "MANITA" (16/10/2026, pedido de SAC: acercar las fotos que mandan los clientes y moverlas):
// rueda del mouse (acerca hacia donde apunta), botones − / % / +, teclas + − 0, clic en la imagen (2.5×)
// y pellizco en el celular. Con zoom, el cursor es una manita y se arrastra para mover la imagen; nunca
// se puede sacar de la pantalla. Como ahora se arrastra sobre la imagen, ya NO cierra al hacer clic en
// cualquier parte: cierra con la X, con clic AFUERA de la imagen (onMouseDown, regla de los modales),
// con Esc y con el botón atrás. Misma API de siempre: ninguna pantalla que lo usa tuvo que cambiar.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, XMarkIcon } from './Icons';

const ZOOM_MIN = 1;
const ZOOM_MAX = 6;
const ZOOM_CLIC = 2.5;
const PASO_BOTON = 1.5;
const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const SIN_ZOOM = { escala: 1, x: 0, y: 0 };

function ImagenLightbox({ src, nombre, onClose }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const fondoRef = useRef(null);
  const imgRef = useRef(null);
  // escala y traslado de la imagen. La ref es la fuente de verdad para los gestos (varios eventos llegan
  // antes de que React vuelva a dibujar); el estado solo dibuja.
  const [vista, setVista] = useState(SIN_ZOOM);
  const vistaRef = useRef(SIN_ZOOM);
  const [arrastrando, setArrastrando] = useState(false);
  const punteros = useRef(new Map());   // dedos / mouse apoyados: pointerId → { x, y }
  const gesto = useRef(null);
  const movio = useRef(false);          // si hubo arrastre, el clic que viene después no hace zoom

  const aplicar = useCallback((v) => { vistaRef.current = v; setVista(v); }, []);

  // Cada imagen nueva arranca ajustada a la pantalla.
  useEffect(() => { aplicar(SIN_ZOOM); }, [src, aplicar]);

  // Centro de la imagen SIN zoom ni traslado, medido por su lugar en la página (no por lo dibujado: la rueda
  // manda varios eventos antes de que se vuelva a dibujar, y medir lo dibujado corría el punto del zoom).
  const centroBase = useCallback(() => {
    const img = imgRef.current;
    const r = fondoRef.current ? fondoRef.current.getBoundingClientRect() : { left: 0, top: 0 };
    return { x: r.left + img.offsetLeft + img.offsetWidth / 2, y: r.top + img.offsetTop + img.offsetHeight / 2 };
  }, []);

  // Con zoom, la imagen se mueve hasta que su borde toca el de la pantalla, nunca más allá. Si en un sentido
  // todavía cabe entera, se puede correr hacia donde se acerca pero sin que ninguna parte quede afuera.
  const acomodar = useCallback((escala, x, y) => {
    const s = limitar(escala, ZOOM_MIN, ZOOM_MAX);
    const img = imgRef.current;
    if (s <= 1.001 || !img) return SIN_ZOOM;
    const b = centroBase();
    const ancho = img.offsetWidth * s;
    const alto = img.offsetHeight * s;
    // Los dos topes de cada eje: borde izquierdo/superior en 0, o borde derecho/inferior en el de la pantalla.
    const entre = (v, a, c) => limitar(v, Math.min(a, c), Math.max(a, c));
    return {
      escala: s,
      x: entre(x, ancho / 2 - b.x, window.innerWidth - b.x - ancho / 2),
      y: entre(y, alto / 2 - b.y, window.innerHeight - b.y - alto / 2)
    };
  }, [centroBase]);

  // Acerca o aleja dejando quieto el punto (px, py) de la pantalla; sin punto, el centro de la imagen.
  const zoomEn = useCallback((nuevaEscala, px, py) => {
    const img = imgRef.current;
    if (!img) return;
    const v = vistaRef.current;
    const { x: baseX, y: baseY } = centroBase();
    const s2 = limitar(nuevaEscala, ZOOM_MIN, ZOOM_MAX);
    const mx = (px == null ? baseX + v.x : px) - baseX;
    const my = (py == null ? baseY + v.y : py) - baseY;
    const k = s2 / v.escala;
    aplicar(acomodar(s2, mx - k * (mx - v.x), my - k * (my - v.y)));
  }, [acomodar, aplicar, centroBase]);

  // History API: el botón atrás del teléfono cierra el lightbox (no la vista de atrás).
  // 🔴 Conservamos el estado previo (ej. el { modalId } del <Modal> padre cuando el
  // lightbox se abre DENTRO de un modal). Si lo pisáramos con solo { lightbox: true },
  // al cerrar el lightbox el popstate del <Modal> vería "sin modalId" y se cerraría de
  // más, sacando al usuario hasta la tabla. Preservando el modalId, el modal padre
  // sobrevive al cierre del lightbox.
  useEffect(() => {
    if (!src) return undefined;
    window.history.pushState({ ...(window.history.state || {}), lightbox: true }, '');
    const handlePop = () => { if (onCloseRef.current) onCloseRef.current(); };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [src]);

  const cerrar = useCallback(() => {
    if (window.history.state && window.history.state.lightbox) {
      window.history.back();          // dispara popstate → onClose
    } else if (onCloseRef.current) {
      onCloseRef.current();
    }
  }, []);

  // Rueda del mouse (y pellizco del touchpad): acerca hacia donde apunta. Escuchador propio con
  // passive:false — el de React es pasivo y no deja frenar el scroll de la página de atrás.
  useEffect(() => {
    const el = fondoRef.current;
    if (!src || !el) return undefined;
    const alGirar = (e) => {
      e.preventDefault();
      zoomEn(vistaRef.current.escala * Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
    };
    el.addEventListener('wheel', alGirar, { passive: false });
    return () => el.removeEventListener('wheel', alGirar);
  }, [src, zoomEn]);

  // Teclado: Esc cierra; + / − acercan y alejan; 0 vuelve a ajustar.
  useEffect(() => {
    if (!src) return undefined;
    const alTeclear = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cerrar(); }
      else if (e.key === '+' || e.key === '=') zoomEn(vistaRef.current.escala * PASO_BOTON);
      else if (e.key === '-') zoomEn(vistaRef.current.escala / PASO_BOTON);
      else if (e.key === '0') aplicar(SIN_ZOOM);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [src, zoomEn, cerrar, aplicar]);

  // Un dedo (o el mouse) mueve la imagen cuando hay zoom; dos dedos la acercan o alejan.
  const empezarArrastre = (p) => {
    gesto.current = { tipo: 'mover', x0: p.x, y0: p.y, tx: vistaRef.current.x, ty: vistaRef.current.y };
  };
  const alBajar = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    if (punteros.current.size === 1) {
      movio.current = false;
      empezarArrastre({ x: e.clientX, y: e.clientY });
      if (vistaRef.current.escala > 1) setArrastrando(true);
    } else if (punteros.current.size === 2) {
      const [a, b] = [...punteros.current.values()];
      gesto.current = { tipo: 'pellizco', dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, escala: vistaRef.current.escala };
      movio.current = true;
      setArrastrando(true);   // sin la transición, el pellizco sigue al dedo sin retraso
    }
  };
  const alMover = (e) => {
    if (!punteros.current.has(e.pointerId)) return;
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesto.current;
    if (!g) return;
    if (g.tipo === 'pellizco' && punteros.current.size >= 2) {
      const [a, b] = [...punteros.current.values()];
      zoomEn(g.escala * (Math.hypot(a.x - b.x, a.y - b.y) / g.dist), (a.x + b.x) / 2, (a.y + b.y) / 2);
    } else if (g.tipo === 'mover') {
      const dx = e.clientX - g.x0;
      const dy = e.clientY - g.y0;
      if (Math.abs(dx) + Math.abs(dy) > 4) movio.current = true;
      if (vistaRef.current.escala > 1) aplicar(acomodar(vistaRef.current.escala, g.tx + dx, g.ty + dy));
    }
  };
  const alSoltar = (e) => {
    punteros.current.delete(e.pointerId);
    if (punteros.current.size === 0) {
      gesto.current = null;
      setArrastrando(false);
    } else if (punteros.current.size === 1) {
      empezarArrastre([...punteros.current.values()][0]);   // levantó un dedo del pellizco: sigue moviendo
    }
  };
  // Clic en la imagen sin zoom: acerca hacia ese punto. Con zoom no hace nada (se está moviendo).
  const alClic = (e) => {
    if (movio.current) return;
    if (vistaRef.current.escala <= 1) zoomEn(ZOOM_CLIC, e.clientX, e.clientY);
  };

  if (!src) return null;

  const conZoom = vista.escala > 1;
  const botonVisor = {
    width: '36px', height: '36px', border: 'none', borderRadius: '999px', cursor: 'pointer',
    background: 'transparent', color: 'rgba(255,255,255,0.9)',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0
  };

  return (
    <div
      ref={fondoRef}
      // Cierra solo con un clic que EMPIEZA afuera de la imagen (regla de los modales): un arrastre que
      // termina sobre el fondo no cierra.
      onMouseDown={(e) => { if (e.target === e.currentTarget) cerrar(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: 'zoom-out', padding: '20px', overflow: 'hidden',
        touchAction: 'none', userSelect: 'none'
      }}
    >
      {/* La X estándar (.modal-close-std: roja al pasar el mouse) sobre un fondo oscuro propio, para que se vea
          también cuando la foto acercada es clara. */}
      <div style={{ position: 'absolute', top: '12px', right: '12px', width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(20,20,20,0.6)', zIndex: 3 }}>
        <button
          type="button"
          className="modal-close-std"
          onClick={cerrar}
          title="Cerrar (Esc)"
          aria-label="Cerrar"
          style={{ color: 'rgba(255,255,255,0.9)' }}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <img
        ref={imgRef}
        src={src}
        alt={nombre || ''}
        draggable={false}
        onPointerDown={alBajar}
        onPointerMove={alMover}
        onPointerUp={alSoltar}
        onPointerCancel={alSoltar}
        onClick={alClic}
        style={{
          position: 'relative', zIndex: 1,
          maxWidth: '90vw', maxHeight: 'calc(100vh - 170px)',
          objectFit: 'contain', borderRadius: conZoom ? 0 : '8px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          transform: `translate(${vista.x}px, ${vista.y}px) scale(${vista.escala})`,
          transformOrigin: 'center center',
          transition: arrastrando ? 'none' : 'transform 0.12s ease-out',
          willChange: 'transform',
          cursor: conZoom ? (arrastrando ? 'grabbing' : 'grab') : 'zoom-in',
          touchAction: 'none'
        }}
      />
      <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '14px', gap: '6px', maxWidth: '90vw' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '3px',
          background: 'rgba(20,20,20,0.75)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '999px'
        }}>
          <button type="button" className="btn-press" style={botonVisor} title="Alejar (−)" aria-label="Alejar"
            onClick={() => zoomEn(vista.escala / PASO_BOTON)} disabled={!conZoom}>
            <span style={{ display: 'inline-flex', opacity: conZoom ? 1 : 0.35 }}><MagnifyingGlassMinusIcon className="w-5 h-5" /></span>
          </button>
          <button type="button" className="btn-press" title="Ajustar a la pantalla (0)" aria-label="Ajustar a la pantalla"
            onClick={() => aplicar(SIN_ZOOM)}
            style={{ ...botonVisor, width: 'auto', minWidth: '58px', padding: '0 8px', fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(vista.escala * 100)}%
          </button>
          <button type="button" className="btn-press" style={botonVisor} title="Acercar (+)" aria-label="Acercar"
            onClick={() => zoomEn(vista.escala * PASO_BOTON)} disabled={vista.escala >= ZOOM_MAX}>
            <span style={{ display: 'inline-flex', opacity: vista.escala >= ZOOM_MAX ? 0.35 : 1 }}><MagnifyingGlassPlusIcon className="w-5 h-5" /></span>
          </button>
        </div>
        {/* Textos sobre un fondo oscuro: con la foto acercada quedan encima de la imagen y se tienen que leer. */}
        {nombre && (
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', textAlign: 'center', background: 'rgba(20,20,20,0.6)', padding: '2px 10px', borderRadius: '999px' }}>
            {nombre}
          </div>
        )}
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', textAlign: 'center', background: 'rgba(20,20,20,0.6)', padding: '2px 10px', borderRadius: '999px' }}>
          {conZoom
            ? 'Arrastrar la imagen para moverla · Clic afuera o Esc para cerrar'
            : 'Clic en la imagen, rueda del mouse o pellizco para acercar · Clic afuera o Esc para cerrar'}
        </div>
      </div>
    </div>
  );
}

export default ImagenLightbox;
